import { BonusType } from './bonuses';
import {
  calculateTicket,
  type BetslipSystemLimits,
  type BetslipTicket,
} from './betslip';
import type { PlaceBetMessage } from './place-bet';

/** UsersAPI `PlayerRewards` — FreeBet / BackupBet available to the player. */
export interface PlayerReward {
  Id: number;
  Name?: string | null;
  Type: number;
  Amount?: number | null;
  MinimumQuota?: number | null;
  MaximumQuota?: number | null;
  StartDate?: string | null;
  EndDate?: string | null;
}

/** Socket `PlayerMessage` Value for Type `Reward`. */
export interface SocketMessageRewards {
  Delete?: boolean;
  Reward?: PlayerReward | null;
}

export function isPlayerRewardType(type: number): boolean {
  return type === BonusType.FreeBet || type === BonusType.BackupBet;
}

/** True when `now` is inside the reward validity window. */
export function isRewardActive(
  reward: PlayerReward,
  now: Date = new Date(),
): boolean {
  if (!isPlayerRewardType(reward.Type)) return false;
  const start = reward.StartDate ? new Date(reward.StartDate) : null;
  const end = reward.EndDate ? new Date(reward.EndDate) : null;
  if (start && Number.isFinite(start.getTime()) && start > now) return false;
  if (end && Number.isFinite(end.getTime()) && end < now) return false;
  return true;
}

/** Active FreeBet / BackupBet rewards for banners and pickers. */
export function activePlayerRewards(
  rewards: PlayerReward[] | null | undefined,
  now: Date = new Date(),
): PlayerReward[] {
  if (!Array.isArray(rewards) || rewards.length === 0) return [];
  return rewards.filter((reward) => isRewardActive(reward, now));
}

export type RewardQuotaCheck =
  | { ok: true }
  | { ok: false; message: PlaceBetMessage };

/** Legacy min/max ticket odds gates before applying a reward. */
export function checkRewardQuota(
  reward: PlayerReward,
  ticketOdds: number | null | undefined,
): RewardQuotaCheck {
  const odds =
    typeof ticketOdds === 'number' && Number.isFinite(ticketOdds) ? ticketOdds : 0;

  const min = reward.MinimumQuota;
  if (typeof min === 'number' && Number.isFinite(min) && odds < min) {
    return {
      ok: false,
      message: {
        key: 'MinimumRewardQuota',
        defaultValue: 'Minimum reward odds',
        limit: min,
        limitKind: 'odds',
      },
    };
  }

  const max = reward.MaximumQuota;
  if (typeof max === 'number' && Number.isFinite(max) && max > 0 && odds > max) {
    return {
      ok: false,
      message: {
        key: 'MaximumRewardQuota',
        defaultValue: 'Maximum reward odds',
        limit: max,
        limitKind: 'odds',
      },
    };
  }

  return { ok: true };
}

export function isEligibleForTicket(
  reward: PlayerReward,
  ticketOdds: number | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!isRewardActive(reward, now)) return false;
  return checkRewardQuota(reward, ticketOdds).ok;
}

/** Apply a reward: lock stake to Amount and set RewardId. */
export function applyRewardToTicket(
  ticket: BetslipTicket,
  reward: PlayerReward,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const amount =
    typeof reward.Amount === 'number' && Number.isFinite(reward.Amount)
      ? reward.Amount
      : ticket.Stake;
  const next = structuredClone(ticket);
  next.RewardId = reward.Id;
  next.Rewards = [reward];
  next.Stake = amount;
  return calculateTicket(next, limits ?? undefined);
}

/** Clear applied reward; stake is left as-is for the caller to reset if needed. */
export function clearRewardFromTicket(
  ticket: BetslipTicket,
  limits?: Partial<BetslipSystemLimits> | null,
): BetslipTicket {
  const next = structuredClone(ticket);
  next.RewardId = null;
  next.Rewards = [];
  return calculateTicket(next, limits ?? undefined);
}

export function appliedRewardFromTicket(
  ticket: BetslipTicket,
): PlayerReward | null {
  if (ticket.RewardId == null) return null;
  const fromList = ticket.Rewards.find((r) => r.Id === ticket.RewardId);
  return fromList ?? ticket.Rewards[0] ?? null;
}

/** Remove a reward by id from a session list (immutable). */
export function removeRewardById(
  rewards: PlayerReward[] | null | undefined,
  id: number,
): PlayerReward[] {
  if (!Array.isArray(rewards) || rewards.length === 0) return [];
  return rewards.filter((r) => r.Id !== id);
}

/** Upsert a reward into the session list (immutable). */
export function upsertPlayerReward(
  rewards: PlayerReward[] | null | undefined,
  reward: PlayerReward,
): PlayerReward[] {
  const list = Array.isArray(rewards) ? [...rewards] : [];
  const index = list.findIndex((r) => r.Id === reward.Id);
  if (index >= 0) {
    list[index] = reward;
  } else {
    list.push(reward);
  }
  return list;
}

/** Normalize unknown login/socket payloads into PlayerReward. */
export function parsePlayerReward(value: unknown): PlayerReward | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const id = record.Id;
  if (typeof id !== 'number' || !Number.isFinite(id)) return null;
  const type = record.Type;
  if (typeof type !== 'number' || !Number.isFinite(type)) return null;
  return {
    Id: id,
    Type: type,
    Name: typeof record.Name === 'string' ? record.Name : null,
    Amount:
      typeof record.Amount === 'number' && Number.isFinite(record.Amount)
        ? record.Amount
        : null,
    MinimumQuota:
      typeof record.MinimumQuota === 'number' && Number.isFinite(record.MinimumQuota)
        ? record.MinimumQuota
        : null,
    MaximumQuota:
      typeof record.MaximumQuota === 'number' && Number.isFinite(record.MaximumQuota)
        ? record.MaximumQuota
        : null,
    StartDate: typeof record.StartDate === 'string' ? record.StartDate : null,
    EndDate: typeof record.EndDate === 'string' ? record.EndDate : null,
  };
}

export function parseSocketMessageRewards(
  value: unknown,
): SocketMessageRewards | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const reward = parsePlayerReward(record.Reward);
  return {
    Delete: Boolean(record.Delete),
    Reward: reward,
  };
}

/** Display label for applied/idle cards. */
export function rewardDisplayName(
  reward: PlayerReward,
  formatAmount: (n: number) => string,
  currency = '',
): string {
  const name = reward.Name?.trim();
  if (name) return name;
  const amount =
    typeof reward.Amount === 'number' && Number.isFinite(reward.Amount)
      ? formatAmount(reward.Amount)
      : '';
  const withCurrency = currency && amount ? `${amount} ${currency}` : amount;
  if (reward.Type === BonusType.BackupBet) {
    return withCurrency ? `Backup Bet ${withCurrency}` : 'Backup Bet';
  }
  return withCurrency ? `Free Bet ${withCurrency}` : 'Free Bet';
}
